import { Show } from "solid-js";
import { IoPlay, IoPause } from "solid-icons/io";

import {
  livePlaying,
  setLivePlaying,
  liveManeuverFollow,
  setLiveManeuverFollow,
  livePerformanceFollow,
  setLivePerformanceFollow,
  liveSummaryFollow,
  setLiveSummaryFollow,
} from "../../store/playbackStore";

/** Which accumulating report page this toggle owns. Required when `scope="report"`. */
export type LiveReportFollowPage = "maneuver" | "performance" | "summary";

export interface LiveFollowToggleProps {
  /** Optional extra class for placement within a host control bar. */
  class?: string;
  /**
   * Which transport this instance drives. Defaults to `'clock'` for the hosts that had no say in it
   * before this prop existed.
   *
   * - `'clock'` — the shared live transport (`isPlaying` + `liveFollow`). Moving-window surfaces: the
   *   live map, the map timeline, the live Time Series. Pressing pause here freezes all of them.
   * - `'report'` — that page's OWN report switch (`liveManeuverFollow` / `livePerformanceFollow` /
   *   `liveSummaryFollow`). Pressing pause here stops new rows merging into THIS page and does
   *   nothing else — the map keeps playing, and a sibling report page keeps following.
   *
   * See the block comment above the report follow signals in playbackStore for why these must not
   * share one signal with each other or with the clock.
   */
  scope?: "clock" | "report";
  /**
   * Which report page owns this toggle. Required when `scope="report"` so Maneuvers / Performance /
   * Fleet Summary each read and write their own follow flag.
   */
  page?: LiveReportFollowPage;
}

/**
 * Compact "follow the live edge" transport for the Live Time Series / Performance / Maneuvers pages.
 *
 * Following ON  → auto-select the latest data as it streams in (page tracks the live edge).
 * Following OFF → the page behaves like a standard Fleet page (full fleet view, manual
 *                 selection/filtering); the live stream/merge work is suspended upstream.
 *
 * WHICH transport depends on `scope` — see the prop docs above, and the block comment on the report
 * follow signals in playbackStore for why clock and report (and each report page) are separate:
 *
 * - `scope="clock"` (default): drives the shared clock through `setLivePlaying`, so a live map open
 *   beside the live Time Series in split view pauses with it.
 *
 *   There used to be an effect here mirroring every `isPlaying` pause back into follow. It is gone:
 *   the clock is machinery that startup, teardown, zoom holds and chart re-inits stop on their own,
 *   so treating every one of those as "the user paused" made an upstream stall read as PAUSED on a
 *   map nobody had touched. Follow is INTENT and only a gesture writes it; everything else calls
 *   `stopPlaybackClock()`, and a frozen feed is reported by the status pill instead.
 * - `scope="report"`: THIS page's merge switch only (`page` picks which). Pausing to pick some events
 *   leaves the map playing AND leaves the other report page following. No coupling effect runs in
 *   this scope — that is the whole point.
 *
 * Visual box model mirrors DropDownButton (`py-2 px-3`, small label + value) so it sits at the same
 * height as the sibling dropdowns in the control bar.
 */
const LiveFollowToggle = (props: LiveFollowToggleProps) => {
  const isReportScope = () => props.scope === "report";

  const reportFollowing = (): boolean => {
    switch (props.page) {
      case "performance":
        return livePerformanceFollow() === true;
      case "summary":
        return liveSummaryFollow() === true;
      case "maneuver":
      default:
        return liveManeuverFollow() === true;
    }
  };

  const setReportFollowing = (value: boolean): void => {
    switch (props.page) {
      case "performance":
        setLivePerformanceFollow(value);
        return;
      case "summary":
        setLiveSummaryFollow(value);
        return;
      case "maneuver":
      default:
        setLiveManeuverFollow(value);
    }
  };

  const following = () => (isReportScope() ? reportFollowing() : livePlaying()) === true;

  const toggle = () => {
    const next = !following();
    if (isReportScope()) {
      setReportFollowing(next);
      return;
    }
    // Writes both halves of the clock transport, clears a stale manual hold, and snaps to the live
    // edge on resume. See the transport block comment in playbackStore.
    setLivePlaying(next);
  };

  return (
    <button
      type="button"
      class={`dropdown live-follow-toggle py-2 px-3 rounded-md flex flex-col items-start${following() ? " live-follow-toggle--on" : ""}${props.class ? ` ${props.class}` : ""}`}
      onClick={toggle}
      onContextMenu={(e) => e.preventDefault()}
      title={
        isReportScope()
          ? following()
            ? "Following the latest data — click to stop new rows merging into this report. The live map keeps running."
            : "Paused — click to merge new rows into this report as they arrive"
          : following()
            ? "Following the latest data — click to pause and browse freely"
            : "Paused — click to follow the latest data as it arrives"
      }
      aria-pressed={following()}
      aria-label={following() ? "Pause live follow" : "Follow live"}
    >
      <span class="self-start text-xs font-medium">Follow</span>
      <span class="text-sm font-bold live-follow-toggle__value">
        <span class="live-follow-toggle__icon" aria-hidden="true">
          <Show when={following()} fallback={<IoPlay />}>
            <IoPause />
          </Show>
        </span>
        {following() ? "LIVE" : "Paused"}
      </span>
    </button>
  );
};

export default LiveFollowToggle;
